// Danish field (property) labels for the Schema Markup plug-in.
//
// Flat map of schema.org property name -> Danish label. The KEY is the schema.org
// property (written verbatim into the JSON-LD) and must match schema.org exactly;
// edit only the label (the value). A property shared by several types appears ONCE
// (a flat object cannot have duplicate keys) - the comment groups below show which
// schema type first introduces each field.
//
// Covers the fields of every type in ROOT_TYPES (see ./types.js).

export const LABEL_TRANSLATIONS = {
    // --- Common (Thing) ---
    name: "Navn",
    alternateName: "Alternativt navn",
    description: "Beskrivelse",
    disambiguatingDescription: "Præciserende beskrivelse",
    additionalType: "Yderligere type",
    identifier: "Identifikator",
    url: "URL",
    image: "Billede",
    sameAs: "Samme som (URL)",
    keywords: "Nøgleord",
    mainEntity: "Hovedenhed",
    mainEntityOfPage: "Hovedindhold på siden",
    subjectOf: "Omtalt i",

    // --- Content (CreativeWork / Article / WebPage) ---
    headline: "Overskrift",
    articleBody: "Brødtekst",
    articleSection: "Artikelsektion",
    abstract: "Resumé",
    text: "Tekst",
    wordCount: "Antal ord",
    pagination: "Sidetal",
    pageStart: "Startside",
    pageEnd: "Slutside",
    inLanguage: "Sprog",
    about: "Handler om",
    audience: "Målgruppe",
    citation: "Kildehenvisning",
    license: "Licens",
    isPartOf: "Del af",
    hasPart: "Indeholder",
    mainContentOfPage: "Sidens hovedindhold",
    primaryImageOfPage: "Primært billede",
    relatedLink: "Relateret link",
    significantLink: "Væsentligt link",
    breadcrumb: "Brødkrumme",
    speakable: "Oplæselige dele",
    creativeWorkStatus: "Status (indhold)",
    copyrightHolder: "Rettighedshaver",
    copyrightYear: "Copyright-år",

    // --- MedicalWebPage ---
    medicalAudience: "Medicinsk målgruppe",
    aspect: "Aspekt",

    // --- Dates ---
    datePublished: "Udgivelsesdato",
    dateModified: "Ændringsdato",
    dateCreated: "Oprettelsesdato",
    lastReviewed: "Sidst gennemgået",
    uploadDate: "Uploadet",
    startDate: "Startdato",
    endDate: "Slutdato",

    // --- People / roles ---
    author: "Forfatter",
    reviewedBy: "Gennemgået af",
    contributor: "Bidragydere",
    editor: "Redaktør",
    publisher: "Udgiver",
    jobTitle: "Jobtitel",

    // --- Organisations / places (Hospital, MedicalClinic) ---
    address: "Adresse",
    telephone: "Telefon",
    email: "E-mail",
    faxNumber: "Faxnummer",
    openingHours: "Åbningstider",
    geo: "Geografisk placering",
    location: "Placering",
    areaServed: "Dækningsområde",
    department: "Afdeling",
    availableService: "Tilgængelig ydelse",
    isAcceptingNewPatients: "Modtager nye patienter",
    healthPlanNetworkId: "Sundhedsnetværks-id",
    healthcareReportingData: "Sundhedsrapporteringsdata",
    logo: "Logo",
    founder: "Grundlægger",
    foundingDate: "Grundlæggelsesdato",
    priceRange: "Prisniveau",

    // --- MedicalEntity (base for all medical types) ---
    code: "Kode",
    codingSystem: "Kodningssystem",
    funding: "Finansiering",
    guideline: "Retningslinje",
    legalStatus: "Juridisk status",
    medicineSystem: "Medicinsk system",
    recognizingAuthority: "Anerkendende myndighed",
    relevantSpecialty: "Relevant speciale",
    medicalSpecialty: "Medicinsk speciale",
    specialty: "Speciale",
    study: "Studie",

    // --- MedicalCondition ---
    associatedAnatomy: "Tilknyttet anatomi",
    cause: "Årsager",
    differentialDiagnosis: "Differentialdiagnose",
    distinguishingSign: "Kendetegn",
    drug: "Lægemiddel",
    epidemiology: "Epidemiologi",
    expectedPrognosis: "Forventet prognose",
    naturalProgression: "Naturligt forløb",
    pathophysiology: "Patofysiologi",
    possibleComplication: "Mulige komplikationer",
    possibleTreatment: "Mulige behandlinger",
    primaryPrevention: "Primær forebyggelse",
    riskFactor: "Risikofaktorer",
    secondaryPrevention: "Sekundær forebyggelse",
    signOrSymptom: "Tegn eller symptomer",
    stage: "Stadie",
    status: "Status",
    subtype: "Undertype",
    typicalTest: "Typisk undersøgelse",

    // --- AnatomicalStructure ---
    associatedPathophysiology: "Tilknyttet patofysiologi",
    bodyLocation: "Placering i kroppen",
    connectedTo: "Forbundet med",
    diagram: "Diagram",
    partOfSystem: "Del af system",
    relatedCondition: "Relateret tilstand",
    relatedTherapy: "Relateret behandling",
    subStructure: "Understruktur",

    // --- MedicalSignOrSymptom ---
    identifyingExam: "Identificerende undersøgelse",
    identifyingTest: "Identificerende test",

    // --- MedicalRiskFactor ---
    increasesRiskOf: "Øger risiko for",

    // --- MedicalTest ---
    affectedBy: "Påvirkes af",
    normalRange: "Normalområde",
    signDetected: "Påvist tegn",
    usedToDiagnose: "Bruges til at diagnosticere",
    usesDevice: "Anvendt udstyr",

    // --- MedicalTherapy / MedicalProcedure ---
    adverseOutcome: "Bivirkning",
    contraindication: "Kontraindikation",
    duplicateTherapy: "Overlappende behandling",
    seriousAdverseOutcome: "Alvorlig bivirkning",
    indication: "Indikation",
    followup: "Opfølgning",
    howPerformed: "Sådan udføres det",
    preparation: "Forberedelse",
    procedureType: "Proceduretype",

    // --- Drug ---
    activeIngredient: "Aktivt stof",
    administrationRoute: "Indgivelsesvej",
    alcoholWarning: "Alkoholadvarsel",
    availableStrength: "Tilgængelig styrke",
    breastfeedingWarning: "Amningsadvarsel",
    clinicalPharmacology: "Klinisk farmakologi",
    dosageForm: "Doseringsform",
    doseSchedule: "Doseringsplan",
    drugClass: "Lægemiddelklasse",
    drugUnit: "Lægemiddelenhed",
    foodWarning: "Fødevareadvarsel",
    interactingDrug: "Interagerende lægemiddel",
    isAvailableGenerically: "Findes som generisk",
    isProprietary: "Er mærkevare",
    labelDetails: "Indlægsseddel",
    maximumIntake: "Maksimalt indtag",
    mechanismOfAction: "Virkningsmekanisme",
    nonProprietaryName: "Generisk navn",
    overdosage: "Overdosering",
    pregnancyCategory: "Graviditetskategori",
    pregnancyWarning: "Graviditetsadvarsel",
    prescribingInfo: "Ordinationsinformation",
    prescriptionStatus: "Receptstatus",
    proprietaryName: "Handelsnavn",
    relatedDrug: "Relateret lægemiddel",
    rxcui: "RxCUI-kode",
    warning: "Advarsel",

    // --- MedicalGuideline ---
    evidenceLevel: "Evidensniveau",
    evidenceOrigin: "Evidensgrundlag",
    guidelineDate: "Retningslinjedato",
    guidelineSubject: "Emne for retningslinje",
    recommendationStrength: "Anbefalingsstyrke",

    // --- MedicalStudy / MedicalTrial ---
    healthCondition: "Sundhedstilstand",
    sponsor: "Sponsor",
    studyLocation: "Studiested",
    studySubject: "Studieemne",
    trialDesign: "Forsøgsdesign",
    phase: "Fase",
    studyDesign: "Studiedesign",

    // --- DietarySupplement ---
    background: "Baggrund",
    recommendedIntake: "Anbefalet indtag",
    safetyConsideration: "Sikkerhedshensyn",
    targetPopulation: "Målgruppe (population)",

    // --- Diet ---
    dietFeatures: "Kostens kendetegn",
    endorsers: "Anbefalet af",
    expertConsiderations: "Ekspertvurderinger",
    physiologicalBenefits: "Fysiologiske fordele",
    risks: "Risici",

    // --- Recipe / HowTo ---
    recipeCategory: "Opskriftskategori",
    recipeCuisine: "Køkken",
    recipeIngredient: "Ingrediens",
    recipeInstructions: "Fremgangsmåde",
    recipeYield: "Antal portioner",
    cookTime: "Tilberedningstid",
    prepTime: "Forberedelsestid",
    performTime: "Aktiv tid",
    totalTime: "Samlet tid",
    cookingMethod: "Tilberedningsmetode",
    nutrition: "Næringsindhold",
    suitableForDiet: "Egnet til diæt",
    step: "Trin",
    tool: "Redskab",
    supply: "Materialer",
    estimatedCost: "Anslået pris",
    yield: "Udbytte",

    // --- FAQPage (Question / Answer) ---
    acceptedAnswer: "Accepteret svar",
    suggestedAnswer: "Foreslået svar",
    answerCount: "Antal svar",
    upvoteCount: "Antal upvotes",
    question: "Spørgsmål",
    answer: "Svar"
}
